import { useRef, useState } from 'react';
import { Check, Download, Loader2, PackageSearch } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { mobileLayer } from '@/lib/mobile-layer';
import type { DrilldownOrigin, DrilldownQuery } from '@/lib/orders-drilldown';
import {
  OrdersDrilldownCustomerCard,
  OrdersDrilldownOrderCard,
} from '@/components/orders-drilldown/orders-drilldown-cards';
import type { OrdersDrilldownDialogProps } from '@/components/orders-drilldown/orders-drilldown-dialog';
import { OrdersDrilldownStats } from '@/components/orders-drilldown/orders-drilldown-stats';
import { OrdersDrilldownToolbar } from '@/components/orders-drilldown/orders-drilldown-toolbar';
import { useOrdersDrilldownCsv } from '@/components/orders-drilldown/use-orders-drilldown-csv';
import { useOrdersDrilldownInfinite } from '@/components/orders-drilldown/use-orders-drilldown-infinite';
import { useOrdersDrilldownViewModel } from '@/components/orders-drilldown/use-orders-drilldown-view-model';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';

/** Opens at half height so the tapped chart stays visible above it. */
const SNAP_HALF = 0.5;
const SNAP_POINTS = [SNAP_HALF, 1];

/**
 * The chart drill-down below 768px (SALES-06 AC 3, 5): a bottom sheet with
 * the same title, numbers, search and Pedidos/Clientes switch as the dialog,
 * the list as cards with "Carregar mais". Same URL state as the dialog; the
 * desktop page (`drillPage`) is ignored and never written here.
 */
export function OrdersDrilldownSheet({
  tenantSlug,
  query: currentQuery,
  origin: currentOrigin,
  granularity,
  periodLabel,
  onSegmentChange,
  onViewChange,
  onSearchChange,
  onClose,
}: OrdersDrilldownDialogProps) {
  const { LL } = useI18nContext();

  // On close the parent drops the query at once; keep rendering the last one
  // so the sheet slides out with its content instead of blanking first.
  const lastQuery = useRef<DrilldownQuery | null>(null);
  if (currentQuery !== null) lastQuery.current = currentQuery;
  const query = currentQuery ?? lastQuery.current;
  const lastOrigin = useRef<DrilldownOrigin | null>(null);
  if (currentOrigin !== null) lastOrigin.current = currentOrigin;
  const origin = currentOrigin ?? lastOrigin.current;

  const open = currentQuery !== null;
  const [snap, setSnap] = useState<number | string | null>(SNAP_HALF);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSnap(SNAP_HALF);
  }

  const list = useOrdersDrilldownInfinite({ tenantSlug, query });
  const pages = list.data?.pages ?? [];
  const firstPage = pages.at(0);
  const view = query?.view ?? 'orders';
  const search = query?.search ?? null;
  // A failed "Carregar mais" keeps the loaded cards; only a failed first page
  // replaces the list with the error.
  const isError = list.isError && firstPage === undefined;

  const csv = useOrdersDrilldownCsv({ tenantSlug, query, origin });
  const { title, stats, emptyText, isEmpty, canExport, isRepurchase } =
    useOrdersDrilldownViewModel({
      query,
      origin,
      granularity,
      periodLabel,
      summary: firstPage?.summary,
      total: firstPage?.total,
      isError,
    });

  const orders = pages.flatMap((page) =>
    page.view === 'orders' ? page.items : []
  );
  const customers = pages.flatMap((page) =>
    page.view === 'customers' ? page.items : []
  );

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      snapPoints={SNAP_POINTS}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      fadeFromIndex={1}
    >
      <DrawerContent className="h-[96dvh] max-h-none data-[state=open]:animate-sheet-snap-in">
        <div className="flex shrink-0 flex-col gap-3 border-b px-4 pb-3">
          <div className="flex items-start justify-between gap-3">
            <DrawerTitle className="min-w-0 text-base leading-snug">
              {title}
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {view === 'customers'
                ? LL.metricsPage.repurchaseDrilldown.customersDescription()
                : LL.metricsPage.repurchaseDrilldown.description()}
            </DrawerDescription>
            <Button
              variant="outline"
              size="icon"
              className={`shrink-0 ${mobileLayer.touch}`}
              disabled={!canExport || csv.isLoading}
              aria-label={LL.metricsPage.repurchaseDrilldown.exportCsv()}
              onClick={() => void csv.download()}
            >
              {csv.isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : csv.isDone ? (
                <Check className="size-4 text-green-500" />
              ) : (
                <Download className="size-4" />
              )}
            </Button>
          </div>
          <OrdersDrilldownStats stats={stats} />
          {query !== null && origin !== null ? (
            <OrdersDrilldownToolbar
              segment={origin}
              isRepurchase={isRepurchase}
              view={view}
              search={search}
              onSegmentChange={onSegmentChange}
              onViewChange={onViewChange}
              onSearchChange={onSearchChange}
            />
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3">
          {isError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <p className="text-sm text-muted-foreground">
                {view === 'customers'
                  ? LL.metricsPage.repurchaseDrilldown.customersError()
                  : LL.metricsPage.repurchaseDrilldown.error()}
              </p>
              <Button
                variant="outline"
                className={mobileLayer.touch}
                onClick={() => void list.refetch()}
              >
                {LL.metricsPage.repurchaseDrilldown.retry()}
              </Button>
            </div>
          ) : list.isPending ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-[92px] rounded-mobile-card" />
              ))}
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <PackageSearch className="size-7" aria-hidden="true" />
              </div>
              <p className="max-w-sm text-sm text-muted-foreground">
                {emptyText}
              </p>
            </div>
          ) : (
            <>
              <ul className="flex flex-col gap-2">
                {view === 'customers'
                  ? customers.map((item) => (
                      <li key={item.key}>
                        <OrdersDrilldownCustomerCard item={item} />
                      </li>
                    ))
                  : orders.map((item) => (
                      <li key={item.id}>
                        <OrdersDrilldownOrderCard
                          item={item}
                          isRepurchase={isRepurchase}
                        />
                      </li>
                    ))}
              </ul>
              {list.hasNextPage ? (
                <Button
                  type="button"
                  variant="outline"
                  className={mobileLayer.touch}
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {list.isFetchingNextPage && (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  )}
                  {list.isFetchNextPageError
                    ? LL.metricsPage.repurchaseDrilldown.retry()
                    : LL.metricsPage.repurchaseDrilldown.mobile.loadMore()}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
